import React, { useState } from 'react';
import styled from 'styled-components';

import Message from './Message';
import MyRating from './star-rating/MyRating';
import ContactFormScreen from './contact-form/ContactFormScreen';
import { widgetContainer } from '@/styles/theme';
import type { MessageScreenProps, Rating } from '@/types';

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  align-items: center;
  min-width: 0;
  width: 100%;
`;

const OuterContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  flex: 1;
  min-width: 0;
  width: 100%;
`;

const InnerContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 0;
  width: 100%;

  ${widgetContainer.wide} {
    flex-direction: row;
    justify-content: center;
  }

  p {
    max-width: 400px;
    text-align: center;
    color: ${({ theme }) => theme.colors.text};
    font-size: 14px;
    overflow-wrap: break-word;

    ${widgetContainer.wide} {
      font-size: 16px;
    }

    a {
      text-decoration: underline;
      color: ${({ theme }) => theme.colors.link};

      &:hover {
        cursor: pointer;
        color: ${({ theme }) => theme.colors.linkHover};
      }
    }
  }
`;

const Bottom = styled.div`
  display: flex;
  flex-direction: column-reverse;
  justify-content: center;
  align-items: center;

  ${widgetContainer.wide} {
    flex-direction: row;
  }
`;

const ContactFormContainer = styled.div`
  margin-top: 20px;
  width: 100%;
  min-width: 0;

  ${widgetContainer.wide} {
    margin-left: 30px;
    margin-top: 0;
    width: auto;
  }
`;

const MessageScreen: React.FC<MessageScreenProps> = ({
  primaryColor,
  message,
  rating,
  resetStarRating,
  sendMessage,
  happy,
  formOnly = false,
  children,
  site,
}) => {
  const [showForm, setShowForm] = useState(false);
  const toggleShowForm = () => setShowForm(!showForm);

  const addressInPrivateMessage = (
    <p>
      If you have concerns you wish to address in private, reach out.{' '}
      <a onClick={toggleShowForm}>Contact Us</a>
    </p>
  );

  /** Form-only unhappy flow configured by the review generation template. */
  if (formOnly) {
    return (
      <Wrapper>
        <OuterContainer>
          <InnerContainer>
            <Message color={primaryColor}>{message}</Message>
            <ContactFormContainer>
              <ContactFormScreen
                primaryColor={primaryColor}
                sendMessage={sendMessage}
                unhappyContact=""
              />
            </ContactFormContainer>
          </InnerContainer>
        </OuterContainer>
        <Bottom>
          <MyRating
            rating={{ stars_count: rating } as Rating}
            resetStarRating={resetStarRating}
          />
        </Bottom>
      </Wrapper>
    );
  }

  /** Customizations for Chicago Crime Tours */
  if (site === 195) {
    return (
      <Wrapper>
        <OuterContainer>
          <InnerContainer>
            {showForm ? (
              <ContactFormContainer>
                <ContactFormScreen
                  primaryColor={primaryColor}
                  sendMessage={sendMessage}
                  unhappyContact=""
                />
              </ContactFormContainer>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                {children}
                <div style={{ marginTop: '50px' }}>
                  <Message color={primaryColor} vertical={true}>
                    {message}
                  </Message>
                  <p>
                    <a onClick={toggleShowForm}>Contact Us</a>
                  </p>
                </div>
              </div>
            )}
          </InnerContainer>
        </OuterContainer>
      </Wrapper>
    );
  }

  return (
    <Wrapper>
      <OuterContainer>
        <InnerContainer>
          {!showForm && <Message color={primaryColor}>{message}</Message>}
          {showForm ? (
            <div>
              <ContactFormScreen
                primaryColor={primaryColor}
                sendMessage={sendMessage}
                unhappyContact=""
              />
              <p>
                If you do not wish to address your concerns here and prefer to
                post a review, <a onClick={toggleShowForm}>click here</a>.
              </p>
            </div>
          ) : (
            <div>
              {!happy && addressInPrivateMessage}
              {children}
              {happy && addressInPrivateMessage}
            </div>
          )}
        </InnerContainer>
      </OuterContainer>
      <Bottom>
        <MyRating
          rating={{ stars_count: rating } as Rating}
          resetStarRating={resetStarRating}
        />
      </Bottom>
    </Wrapper>
  );
};

export default MessageScreen;
